/**
 * Created by katarinababic on 22.4.25.
 */

import { runInAction, makeAutoObservable } from 'mobx';
import { AuthenticationStore } from './AuthenticationStore';
import { LocalizationStore } from './System/LocalizationStore';
import { AnalyticsStore } from './Firebase/AnalyticsStore';
import { WorkoutLog } from '../Model/WorkoutLog/WorkoutLog';
import { MicroCycleDay, MicroCycleDayApi } from '../Model/Coach/Program/MicroCycleDay';
import { ViewWorkout } from '../Model/WorkoutLog/ViewWorkout';
import { CoachWorkout, createCoachWorkout } from '../Model/Coach/Program/Workout/CoachWorkout';
import { EventEntry, EventEntryApi } from '../Model/Coach/Schedule/EventEntry';
import { ViewSet } from '../Model/WorkoutLog/ViewSet';
import { ViewBlock } from '../Model/WorkoutLog/ViewBlock';
import { ActivityLog, ActivityLogApi } from '../Model/Activity/ActivityLog';
import { WorkoutRunner } from './WorkoutRunner';
import { CoachWorkoutApi } from '../Model/Coach/Program/Workout/CoachWorkout';

function createWorkoutViewStore(
  authentication: AuthenticationStore,
  workoutRunner: WorkoutRunner,
  language: LocalizationStore,
  analytics: AnalyticsStore,
) {
  const store = makeAutoObservable({
    authentication,
    workoutRunner,
    language,
    analytics,
    restorePromise: undefined as Promise<any> | undefined,
    endWorkoutOverviewModalVisible: false,
    workoutLog: undefined as WorkoutLog | undefined,

    restoreWorkout(workoutLog: WorkoutLog): Promise<any> {
      if (!this.restorePromise) {
        if (workoutLog.microCycleDayId && workoutLog.workoutId) {
          this.restorePromise = Promise.all([
            CoachWorkoutApi.get(workoutLog.workoutId),
            MicroCycleDayApi.findOne(workoutLog.microCycleDayId),
          ])
            .then(([workout, microCycleDay]) => {
              if (workout) {
                runInAction(() => {
                  workoutLog.setWorkout(workout);
                  if (microCycleDay) {
                    workoutLog.setMicroCycleDay(microCycleDay);
                  }
                  this.workoutRunner.replay(workoutLog, workout);
                  const nextSet =
                    this.viewWorkout.activeViewSet ??
                    this.viewWorkout.currentViewSetFromEnd ??
                    this.viewWorkout.firstViewBlock?.firstSet;
                  nextSet && this.viewWorkout.selectViewSet(nextSet);
                });
              }
            })
            .finally(() => runInAction(() => (this.restorePromise = undefined)));
        }
        return Promise.resolve();
      }

      return this.restorePromise;
    },

    selectMicroCycleDay(microCycleDay: MicroCycleDay): Promise<ViewWorkout | undefined> {
      const { athlete } = this.authentication;
      if (athlete) {
        const workout = microCycleDay.workout ?? createCoachWorkout();
        return this.workoutRunner.prepareWorkout(athlete, workout, microCycleDay);
      }
      return Promise.resolve(undefined);
    },

    selectWorkoutFromExploreTabTemplate(
      coachWorkout: CoachWorkout,
      eventEntry?: EventEntry,
    ): Promise<ViewWorkout | undefined> {
      if (this.authentication.athlete) {
        return this.workoutRunner.prepareWorkout(this.authentication.athlete, coachWorkout, undefined, eventEntry);
      }
      return Promise.resolve(undefined);
    },

    prepareWorkoutStart() {
      const { workoutLog } = this.viewWorkout;
      if (workoutLog.endDateTime) {
        workoutLog.startDateTime = undefined;
        workoutLog.endDateTime = undefined;
      }
      if (!this.viewWorkout.active) {
        this.startFirstExercise();
      }
    },

    startFirstExercise(): ViewSet | undefined {
      const {
        viewWorkout: { firstViewBlock },
      } = this;
      const firstSet = firstViewBlock?.firstSet;
      if (firstSet) {
        this.viewWorkout.selectViewSet(firstSet);
        return this.startSet(firstSet);
      }
      return undefined;
    },

    startExercise(viewBlock: ViewBlock, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      return this.workoutRunner.startExercise(viewBlock, startDateTime, save);
    },

    startSet(viewSet: ViewSet): ViewSet | undefined {
      return this.workoutRunner.startSet(viewSet);
    },

    onFinishWorkout() {
      // TODO change to window alert
    },

    stopWorkout() {
      const { workoutLog } = this.workoutRunner;
      if (!workoutLog.endDateTime) {
        this.workoutRunner.endWorkout();
      }
      this.showEndWorkoutOverviewModal(workoutLog);
    },

    terminateAndSave(): Promise<WorkoutLog> {
      return this.workoutRunner.terminateAndSave().then((log) => {
        const activityLog = ActivityLogApi.fromWorkoutLog(log);
        if (log.eventEntry) {
          EventEntryApi.complete(log.eventEntry, {
            workoutId: log.workoutId,
            workoutLogId: log.id,
            activityLogId: activityLog.id,
          });
        }
        return log;
      });
    },

    showEndWorkoutOverviewModal(workoutLog: WorkoutLog) {
      this.endWorkoutOverviewModalVisible = true;
      this.workoutLog = workoutLog;
    },

    get viewWorkout(): ViewWorkout {
      return this.workoutRunner.viewWorkout;
    },

    get isWorkoutActive(): boolean {
      return this.viewWorkout.active;
    },

    get isBodyWeightWorkout() {
      return this.viewWorkout.isBodyWeight;
    },

    async syncWorkoutLogWithHealthKit(workoutLog: WorkoutLog): Promise<WorkoutLog> {
      return workoutLog;
    },
  });

  return store;
}

export type WorkoutViewStore = ReturnType<typeof createWorkoutViewStore>;
export const createWorkoutViewStoreInstance = createWorkoutViewStore;
